import { useCallback, useEffect, useState } from 'react';
import { getHealth } from '@crestrewards/api-client';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@crestrewards/ui';
import { AlertCircle, RefreshCw } from 'lucide-react';

import PageContainer from '../../shared/layout/PageContainer';
import StatCard from './StatCard';
import * as dashboardApi from './dashboardApi';
import {
  actionLabel,
  relativeTime,
  ALARMING_ACTIONS,
} from '../../shared/constants/auditActions';

/**
 * The Super Admin landing screen.
 *
 * It answers two questions, in this order: **is anything waiting on me?** and
 * **what does the platform currently hold?** The "needs attention" row comes
 * first for that reason — a half-configured client is the thing that costs
 * money, and a count of partners is not.
 *
 * It used to show a build roadmap ("Phase 6 is complete, Phase 7 is next").
 * That was useful while the console was being built and useless the moment it
 * was in daily use, so it is gone — see FRONTEND_PROGRESS.md.
 */
export default function DashboardPage() {
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    // Settled, not all: the health check failing must not blank the numbers,
    // and vice versa. They are independent facts about the platform.
    const [overviewResult, healthResult] = await Promise.allSettled([
      dashboardApi.getOverview(),
      getHealth(),
    ]);

    if (overviewResult.status === 'fulfilled') {
      setOverview(overviewResult.value);
    } else {
      setOverview(null);
      setError(overviewResult.reason.message);
    }

    setHealth(healthResult.status === 'fulfilled' ? healthResult.value : null);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clients = overview?.clients;
  const partners = overview?.partners;
  const catalog = overview?.catalog;
  const activity = overview?.activity;
  const connected = Boolean(health);

  /** Setup gaps worth surfacing. Only rendered when at least one is non-zero. */
  const attention = [
    {
      key: 'mapping',
      label: 'Clients with no partner mapping',
      value: clients?.withoutPartnerMapping ?? 0,
      to: '/clients',
      note: 'Cannot send SMS or pay out',
    },
    {
      key: 'thresholds',
      label: 'Clients with no limits set',
      value: clients?.withoutThresholds ?? 0,
      to: '/security-parameters',
      note: 'Scans and withdrawals are uncapped',
    },
    {
      key: 'templates',
      label: 'Clients with no QR design',
      value: clients?.withoutQrTemplate ?? 0,
      to: '/qr-templates',
      note: 'Nothing to print',
    },
    {
      key: 'ending',
      label: 'Programs ending in 30 days',
      value: clients?.programsEndingSoon ?? 0,
      to: '/clients',
      note: 'Renew, extend or let lapse',
    },
    {
      key: 'ended',
      label: 'Programs past their end date',
      value: clients?.programsEnded ?? 0,
      to: '/clients',
      note: 'Still marked active',
    },
  ].filter((item) => item.value > 0);

  return (
    <PageContainer
      title="Dashboard"
      description="What the platform holds right now, and anything waiting on you."
      actions={
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw aria-hidden="true" />
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      {error ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border-hairline border-destructive/30 bg-destructive-tint px-4 py-3 text-meta text-destructive-on-tint"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">Could not load the platform overview.</p>
            <p className="mt-0.5">{error}</p>
          </div>
        </div>
      ) : null}

      {/* --- Needs attention ------------------------------------------------ */}
      {loading || attention.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-title font-medium text-foreground">Needs attention</h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <StatCard key={index} label="Loading" loading />
                ))
              : attention.map((item) => (
                  <StatCard
                    key={item.key}
                    label={item.label}
                    value={item.value}
                    note={item.note}
                    tone="warning"
                    to={item.to}
                  />
                ))}
          </div>
        </section>
      ) : (
        <div className="rounded-lg border-hairline border-border bg-card px-5 py-4 text-meta text-muted-foreground">
          Nothing needs attention. Every client is mapped to a partner, has limits set and
          has a QR design, and no program is close to its end date.
        </div>
      )}

      {/* --- Clients -------------------------------------------------------- */}
      <section className="space-y-3">
        <h2 className="text-title font-medium text-foreground">Clients</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Total clients" value={clients?.total ?? 0} to="/clients" loading={loading} />
          <StatCard label="Active" value={clients?.active ?? 0} tone="success" loading={loading} />
          <StatCard label="Suspended" value={clients?.inactive ?? 0} loading={loading} />
          <StatCard
            label="Onboarded in 30 days"
            value={clients?.onboardedLast30Days ?? 0}
            loading={loading}
          />
        </div>
      </section>

      {/* --- Partners ------------------------------------------------------- */}
      <section className="space-y-3">
        <h2 className="text-title font-medium text-foreground">Partners</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Total partners" value={partners?.total ?? 0} to="/partners" loading={loading} />
          <StatCard label="Active" value={partners?.active ?? 0} tone="success" loading={loading} />
          <StatCard
            label="In use by a client"
            value={partners?.inUse ?? 0}
            note={partners?.unused ? `${partners.unused} not mapped to anyone` : undefined}
            loading={loading}
          />
          <StatCard
            label="Missing credentials"
            value={partners?.withoutCredentials ?? 0}
            tone={partners?.withoutCredentials ? 'warning' : 'default'}
            note="Cannot make outbound calls"
            loading={loading}
          />
        </div>

        {!loading && partners?.byType?.length ? (
          <Card>
            <CardHeader>
              <CardTitle>By category</CardTitle>
              <CardDescription>How the configured partners break down.</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-border-subtle text-meta">
                {partners.byType.map((row) => (
                  <li
                    key={row.partnerTypeId}
                    className="flex items-center justify-between py-2 first:pt-0 last:pb-0"
                  >
                    <span className="text-foreground">{row.name}</span>
                    <Badge variant="accent" className="tabular-nums">
                      {row.count}
                    </Badge>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </section>

      {/* --- Catalog and activity ------------------------------------------- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Platform catalog</CardTitle>
            <CardDescription>What clients can be given.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : (
              <dl className="grid grid-cols-[1fr,auto] [&>*]:border-b-hairline [&>*]:border-border-subtle [&>*]:py-2.5 [&>*:nth-last-child(-n+2)]:border-b-0 [&>*:nth-last-child(-n+2)]:pb-0">
                <dt className="text-meta text-muted-foreground">Partner categories</dt>
                <dd className="text-sm font-medium tabular-nums text-foreground">
                  {catalog?.partnerTypes.active ?? 0} active
                  {catalog?.partnerTypes.inactive
                    ? ` · ${catalog.partnerTypes.inactive} retired`
                    : ''}
                </dd>

                <dt className="text-meta text-muted-foreground">Features offered</dt>
                <dd className="text-sm font-medium tabular-nums text-foreground">
                  {catalog?.features.active ?? 0} active
                  {catalog?.features.retired ? ` · ${catalog.features.retired} retired` : ''}
                </dd>

                <dt className="text-meta text-muted-foreground">Feature grants</dt>
                <dd className="text-sm font-medium tabular-nums text-foreground">
                  {catalog?.featureGrants.enabled ?? 0} enabled across all clients
                </dd>
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
            <CardDescription>
              {loading
                ? 'Loading…'
                : `${activity?.last24Hours ?? 0} in the last 24 hours · ${activity?.last7Days ?? 0} this week`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-4 w-full" />
                ))}
              </div>
            ) : activity?.recent?.length ? (
              <ul className="divide-y divide-border-subtle text-meta">
                {activity.recent.map((entry) => (
                  <li key={entry.id} className="py-2 first:pt-0 last:pb-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <span
                        className={
                          ALARMING_ACTIONS.has(entry.action)
                            ? 'font-medium text-warning'
                            : 'text-foreground'
                        }
                      >
                        {actionLabel(entry.action)}
                      </span>
                      <span className="shrink-0 text-label text-muted-foreground">
                        {relativeTime(entry.at)}
                      </span>
                    </div>
                    {entry.actorEmail ? (
                      <div className="truncate text-label text-muted-foreground">
                        {entry.actorEmail}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-meta text-muted-foreground">
                Nothing recorded yet. Every sign-in, credential rotation and configuration
                change will appear here.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* --- Service health -------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Service health</CardTitle>
          <CardDescription>The services this console depends on.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : (
            <dl className="grid grid-cols-[auto,1fr] gap-x-6 [&>*]:border-b-hairline [&>*]:border-border-subtle [&>*]:py-2.5 [&>*:nth-last-child(-n+2)]:border-b-0 [&>*:nth-last-child(-n+2)]:pb-0">
              <dt className="text-meta text-muted-foreground">API</dt>
              <dd>
                <Badge variant={connected ? 'success' : 'destructive'}>
                  {connected ? 'Reachable' : 'Unreachable'}
                </Badge>
              </dd>

              <dt className="text-meta text-muted-foreground">Database</dt>
              <dd>
                <Badge variant={health?.database === 'up' ? 'success' : 'destructive'}>
                  {health?.database === 'up' ? 'Up' : 'Down'}
                </Badge>
              </dd>

              <dt className="text-meta text-muted-foreground">Email</dt>
              <dd>
                {/* console-fallback is correct in development and wrong in
                    production, where env.js refuses to boot without SMTP. */}
                <Badge variant={health?.mail === 'configured' ? 'success' : 'warning'}>
                  {health?.mail === 'configured' ? 'SMTP configured' : 'Console fallback'}
                </Badge>
              </dd>

              <dt className="text-meta text-muted-foreground">Environment</dt>
              <dd className="text-sm font-medium text-foreground">{health?.environment ?? '—'}</dd>

              <dt className="text-meta text-muted-foreground">Uptime</dt>
              <dd className="text-sm font-medium tabular-nums text-foreground">
                {health ? `${health.uptimeSeconds}s` : '—'}
              </dd>
            </dl>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
